import React, { useState } from "react";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
    const [form, setForm] = useState({
        contact_name: "",
        email: "",
        institution: "",
        phone: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            // Dedicated contact intake — Genevieve + ops receive the email; submitter set as Reply-To.
            await api.post("/contact", {
                contact_name: form.contact_name,
                email: form.email,
                institution: form.institution || null,
                phone: form.phone || null,
                notes: form.notes || null,
            });
            setDone(true);
            toast.success("Message sent");
        } catch (err) {
            toast.error("Could not send", { description: err?.response?.data?.detail || err.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-background">
            <section className="border-b border-black/10">
                <div className="container-edge py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="overline">Contact</p>
                        <h1 className="mt-3 font-heading font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                            Let's talk.
                        </h1>
                        <p className="mt-6 max-w-md text-muted-foreground text-lg leading-relaxed">
                            Whether it's a single faculty member or your whole campus — we're happy to help.
                        </p>
                        <div className="mt-8 space-y-3">
                            <a href="tel:8135281102" className="flex items-center gap-3 text-lg font-medium hover:text-klein" data-testid="contact-phone">
                                <Phone size={18} className="text-klein" /> 813-528-1102
                            </a>
                            <a href="mailto:gdobson@degreesofsuccessinfo.com" className="flex items-center gap-3 text-base font-medium hover:text-klein break-all" data-testid="contact-email">
                                <Mail size={18} className="text-klein" /> gdobson@degreesofsuccessinfo.com
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {done ? (
                            <div data-testid="contact-success" className="border border-black/15 p-10 bg-secondary">
                                <h3 className="font-heading font-bold text-2xl">Thanks — message sent.</h3>
                                <p className="mt-3 text-muted-foreground">We'll be in touch within one business day.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-black/15 p-8 bg-white" data-testid="contact-form">
                                <Input label="Your name" testId="contact-name" value={form.contact_name} onChange={onChange("contact_name")} required />
                                <Input label="Email" type="email" testId="contact-email-input" value={form.email} onChange={onChange("email")} required />
                                <Input label="Institution" testId="contact-org" value={form.institution} onChange={onChange("institution")} />
                                <Input label="Phone (optional)" testId="contact-phone-input" value={form.phone} onChange={onChange("phone")} />
                                <label className="flex flex-col gap-2 md:col-span-2">
                                    <span className="text-xs uppercase tracking-widest font-semibold">How can we help?</span>
                                    <textarea
                                        rows={5}
                                        data-testid="contact-message"
                                        required
                                        value={form.notes}
                                        onChange={onChange("notes")}
                                        className="w-full bg-white border border-black/20 px-3 py-2.5 text-sm focus:outline-none focus:border-klein focus:ring-2 focus:ring-klein/20"
                                    />
                                </label>
                                <div className="md:col-span-2 flex justify-end">
                                    <button
                                        type="submit"
                                        data-testid="contact-submit"
                                        disabled={busy}
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50"
                                    >
                                        {busy ? "Sending..." : "Send message"} <ArrowRight size={16} />
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Input({ label, testId, ...rest }) {
    return (
        <label className="flex flex-col gap-2">
            <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
            <input
                data-testid={testId}
                {...rest}
                className="w-full bg-white border border-black/20 px-3 py-2.5 text-sm focus:outline-none focus:border-klein focus:ring-2 focus:ring-klein/20"
            />
        </label>
    );
}
